import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import sortBy from 'lodash/sortBy';
import cn from 'classnames';
import { Icon } from 'legacy-packages/ft-cb';
import AutocompleteSelect from 'chameleon/ui-stack/ui/forms/autocomplete-select';
import './styles.scss';

const StudentsSearch = (props) => {
  const {
    classes,
    onStudentSelected,
    selectedSectionId,
    selectedStudentId,
    containerClassName,
  } = props;
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [students, setStudents] = useState(null);
  const [allStudents, setAllStudents] = useState(null);

  useEffect(() => {
    if ((classes || []).length) {
      let allStudentsFound = [];
      let allSections = classes.map(
        ({ id, name, students: sectionStudents }) => {
          let studentsMap = sectionStudents.map(
            ({ id: studentId, firstName, lastName }) => ({
              firstName,
              lastName,
              label: `${firstName} ${lastName}`,
              value: studentId,
            }),
          );
          allStudentsFound = allStudentsFound.concat(studentsMap);
          studentsMap = sortBy(
            studentsMap,
            [
              ({ firstName, lastName, ...rest }) => ({
                ...rest,
                firstName: firstName.toLowerCase(),
                lastName: lastName.toLowerCase(),
              }),
            ],
            ['lastName', 'firstName'],
          );
          return {
            label: name,
            value: id,
            students: studentsMap,
          };
        },
      );
      allSections = sortBy(
        allSections,
        [({ label }) => label.toLowerCase()],
        ['label'],
      );
      if (allSections.length > 1) {
        allSections.unshift({
          label: 'All Classes',
          value: '-1',
        });
      }

      setSections(allSections);
      const defaultSelectedSection = selectedSectionId
        ? allSections.find(({ value }) => value === selectedSectionId) ||
          allSections[0]
        : allSections[0];
      setSelectedSection(defaultSelectedSection);
      allStudentsFound = sortBy(
        allStudentsFound,
        [
          ({ firstName, lastName, ...rest }) => ({
            ...rest,
            firstName: firstName.toLowerCase(),
            lastName: lastName.toLowerCase(),
          }),
        ],
        ['lastName', 'firstName'],
      );
      setAllStudents(allStudentsFound);
    }
  }, [classes]);

  useEffect(() => {
    if (selectedSection) {
      let foundStudents;
      if (selectedSection.value === '-1') {
        foundStudents = allStudents;
      } else {
        foundStudents =
          (sections.find(({ value }) => value === selectedSection.value) || {})
            .students || [];
      }

      setStudents(foundStudents);
      if (foundStudents.length === 1) {
        setSelectedStudent(foundStudents[0]);
      } else if (selectedStudentId) {
        setSelectedStudent(
          foundStudents.find(({ value }) => value === selectedStudentId),
        );
      }
    }
  }, [selectedSection, sections]);

  useEffect(() => {
    if (selectedSection) {
      onStudentSelected(selectedSection, selectedStudent);
    }
  }, [selectedSection, selectedStudent]);

  const onSectionChange = (section) => {
    setSelectedStudent(null);
    setSelectedSection(section);
  };

  const onStudentChange = (student) => {
    setSelectedStudent(student);
  };

  const onStudentCustomReset = () => {
    setSelectedStudent(null);
  };

  const isSearchingMode = !!selectedStudent;
  return (
    <div className={cn('flex gap-7', containerClassName)}>
      <AutocompleteSelect
        data-test-id="sections-dropdown"
        portalContainerClassName="z-overModals"
        listClassName="StudentsSearch--list"
        onChange={onSectionChange}
        value={selectedSection || {}}
        placeholder={{
          label: 'Select a Class Section',
        }}
        options={sections}
      />
      <AutocompleteSelect
        data-test-id="students-dropdown"
        portalContainerClassName="z-overModals"
        listClassName="StudentsSearch--list"
        onChange={onStudentChange}
        customReset={onStudentCustomReset}
        value={
          selectedStudent || {
            label: '',
          }
        }
        placeholder={{
          label: 'Select a Student',
        }}
        options={students || []}
        icon={
          isSearchingMode ? (
            <Icon name="xmark" />
          ) : (
            <Icon
              className="StudentsSearch--students-search-icon"
              name="search"
              size="18px"
            />
          )
        }
        clearable={isSearchingMode}
      />
    </div>
  );
};

StudentsSearch.propTypes = {
  classes: PropTypes.array.isRequired,
  onStudentSelected: PropTypes.func,
  selectedSectionId: PropTypes.string,
  selectedStudentId: PropTypes.string,
  containerClassName: PropTypes.string,
};

StudentsSearch.defaultProps = {
  onStudentSelected: () => {},
  selectedSectionId: '',
  selectedStudentId: '',
  containerClassName: '',
};

export default StudentsSearch;
